import {Button, Card, CardContent, CardHeader, colors, Divider, Grid, Tab, Tabs, Theme} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import useReactRouter from 'use-react-router';
import {checkPermission} from '../../../components/Can/Can';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {Page} from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import {WcvType} from '../../../graphql/logs.datatypes';
import Results from '../../Groups/GroupList/Results';
import AddNote from '../../Logs/AddNote/AddNote';
import getUser from '../getUser.api';
import UserInfo from './UserInfo';
import UserRoles from './UserRoles';
import OtherActions from './OtherActions';
import {LogListContent} from '../../Logs/LogList/LogList';
import anyLoading from '../../../utils/anyLoading';
import {BillingPartyPayingMembersListContent} from '../../BillingParty/BillingPartyPayingMembersList/BillingPartyPayingMembersList';
import {Can} from '../../../components/Can';
import { NotesList } from '../../Notes';
import MergeUser from '../Tabs/Admin/MergeUser/MergeUser';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    tabs: {
        marginTop: theme.spacing(3)
    },
    divider: {
        backgroundColor: colors.grey[300]
    },
    content: {
        marginTop: theme.spacing(3)
    }
}));

const UserDetails = () => {
    const {history, match}: RouteComponentProps<{ id: string }> = useReactRouter();
    const classes = useStyles();
    const {id} = match.params;
    const userData = getUser(Number.parseInt(id));
    const [openModal, setOpenModal] = useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const loadingData = anyLoading(userData);
    const showNotes = checkPermission('log.note');
    const title = 'User Details';
    const [selectedTab, setSelectedTab] = useState('summary');
    const user = userData.data && userData.data.user;

    useEffect(() => {
        if (userData.error) {
            enqueueSnackbar(`There was an error loading User ${id}`, {variant: 'error'});
            history.push('/users');
        }
    }, [userData.error]);

    const tabs = [];
    tabs.push({value: 'summary', label: 'Summary'});
    tabs.push({value: 'logs', label: 'Logs'});
    tabs.push({value: 'groups', label: 'Groups'});
    if (showNotes) tabs.push({value: 'notes', label: 'Notes'});
    tabs.push({value: 'admin', label: 'Admin'});

    return (
        <LoadingOverlay loading={loadingData.loading}>
            <Page className={classes.root} title={title}>
                <PageHeader section="Users" title={title}/>
                <Tabs
                    className={classes.tabs}
                    onChange={(event, value) => setSelectedTab(value)}
                    scrollButtons="auto"
                    value={selectedTab}
                    variant="scrollable"
                >
                    {tabs.map(item => (<Tab key={item.value} label={item.label} value={item.value}/>))}
                </Tabs>

                <Divider className={classes.divider}/>

                <div className={classes.content}>
                    {user && selectedTab === 'summary' && (
                        <Grid container spacing={3}>
                            <Grid item md={4} xs={12}>
                                <UserInfo user={user}/>
                            </Grid>
                            <Can permission={['role.update']}>
                                <Grid item md={4} xs={12}>
                                    <UserRoles user={user}/>
                                </Grid>
                            </Can>
                            <Can role={['super-admin']}>
                                <Grid item md={4} xs={12}>
                                    <OtherActions user={user}/>
                                </Grid>
                            </Can>
                        </Grid>
                    )}
                    {user && selectedTab === 'logs' && <LogListContent user={user}/>}
                    {user && selectedTab === 'groups' && (
                        <React.Fragment>
                            <BillingPartyPayingMembersListContent userId={user.id} isAdmin={true}
                                                                  canEdit={true}/>
                            <br/>
                            <Card style={{marginTop: 10}}>
                                <CardHeader title="3rd Part Admin Groups"/>
                                <CardContent>
                                    <Results
                                        groups={user.associatedGroups ? user.associatedGroups : []}/>
                                </CardContent>
                            </Card>
                        </React.Fragment>
                    )}

                    {user && selectedTab === 'notes' && (
                        <div>
                            <Button
                                color="primary"
                                onClick={() => setOpenModal(true)}
                                size="small"
                                variant="outlined"
                            >
                                Add Note
                            </Button>
                            <NotesList type={"user"} type_id={user.id} />
                            {openModal && (<AddNote onClose={() => setOpenModal(false)} type={WcvType.USER}
                                                    type_id={user.id}/>)}
                        </div>
                    )}

                    {user && selectedTab === "admin" && (
                        <MergeUser user={user} />
                    )}
                </div>
            </Page>
        </LoadingOverlay>
    );
};

export default UserDetails;
